import {ActionTree, MutationTree} from 'vuex'
import {RootState} from '@/store/types'
import axios from 'axios';
import api from "@/service/api";


export interface State {
    total: any,
    surveysFilled: any,
    mailingFilled: any,
    activeStandmans: any,
    createdContacts: any,
    standmansRating: any,
    contactAttributes: { hotLevel: any, category: any },
    productScans: any,
    productByThematics: any,
    standmansContacts: any,
    standmansSurveys: any,
    loading: {
        total: boolean,
        surveysFilled: boolean,
        activeStandmans: boolean,
        createdContacts: boolean,
        standmansRating: boolean,
        contactAttributes: boolean,
        productScans: boolean,
        productByThematics: boolean,
        standmansContacts: boolean,
        standmansSurveys: boolean,
        mailingFilled: boolean,
    },
    error: {
        total: boolean,
        surveysFilled: boolean,
        activeStandmans: boolean,
        createdContacts: boolean,
        standmansRating: boolean,
        contactAttributes: boolean,
        productScans: boolean,
        productByThematics: boolean,
        standmansContacts: boolean,
        standmansSurveys: boolean,
        mailingFilled: boolean,
    }
}

const defaultState: State = {
    total: {
        allScans: 0,
        scansToday: 0,
        timestampShowStart: null,
        totalChart: []
    },
    mailingFilled:null,
    surveysFilled: {
        filledAllSurveys: null,
        shardsWithFilledSurveys: null,
        filedSurveysToday: null,
        filledSurveys: [],
    },
    activeStandmans: {
        activeStandmans: 0,
        standmans: []
    },
    createdContacts: {},
    standmansRating: {
        byCount: [],
        byFilling: []
    },
    contactAttributes: {
        hotLevel: {
            none: 0,
            cold: 0,
            warm: 0,
            hot: 0,
        },
        category: {
            isNew: 0,
            exists: 0,
            none: 0,
        }
    },
    productScans: {
        companyScanCount: 0,
        products: []
    },
    productByThematics: [],
    standmansContacts: [],
    standmansSurveys: [],
    loading: {
        total: false,
        surveysFilled: false,
        activeStandmans: false,
        createdContacts: false,
        standmansRating: false,
        contactAttributes: false,
        productScans: false,
        productByThematics: false,
        standmansContacts: false,
        standmansSurveys: false,
        mailingFilled: false,
    },
    error: {
        total: false,
        surveysFilled: false,
        activeStandmans: false,
        createdContacts: false,
        standmansRating: false,
        contactAttributes: false,
        productScans: false,
        productByThematics: false,
        standmansContacts: false,
        standmansSurveys: false,
        mailingFilled: false,
    }
}

const getters = {
    total: (state: State) => state.total,
    surveysFilled: (state: State) => state.surveysFilled,
    mailingFilled: (state: State) => state.mailingFilled,
    surveysWithChecked: (state: State) => {
        if (!state.surveysFilled.filledSurveys.length) return []
        return state.surveysFilled.filledSurveys.map((survey: any) => {
            return {...survey, checked: false}
        })
    },
    activeStandmans: (state: State) => state.activeStandmans,
    createdContacts: (state: State) => state.createdContacts,
    standmansRating: (state: State) => state.standmansRating,
    contactAttributes: (state: State) => state.contactAttributes,
    productScans: (state: State) => state.productScans,
    productByThematics: (state: State) => state.productByThematics,
    standmansContacts: (state: State) => state.standmansContacts,
    standmansSurveys: (state: State) => state.standmansSurveys,
    loading: (state: State) => state.loading,
    error: (state: State) => state.error
}

const mutations: MutationTree<State> = {
    setLoading(state: State, payload) {
        state.loading = {
            ...state.loading,
            ...payload
        }
    },
    setError(state: State, payload: any) {
        state.error = {
            ...state.error,
            ...payload
        }
    }
}

const actions: ActionTree<State, RootState> = {
    async loadAll({dispatch, state}, payload) {
        if (!state.loading.total) await dispatch('fetchTotal', payload)
        if (!state.loading.surveysFilled) dispatch('fetchSurveys', payload)
        if (!state.loading.activeStandmans) dispatch('fetchActiveStandmans', payload)
        if (!state.loading.createdContacts) dispatch('fetchCreatedContacts', payload)
        if (!state.loading.standmansRating) dispatch('fetchStandmansRating', payload)
        if (!state.loading.contactAttributes) dispatch('fetchShardAttributes', payload)
        if (!state.loading.productScans) dispatch('fetchProductScans', payload)
        if (!state.loading.mailingFilled) dispatch('fetchMailing', payload)
    },
    loadPdf({dispatch, state}, payload) {
        if (!state.loading.total) dispatch('fetchTotal', payload)
        if (!state.loading.createdContacts) dispatch('fetchCreatedContacts', payload)
        if (!state.loading.contactAttributes) dispatch('fetchShardAttributes', payload)
        if (!state.loading.standmansRating) dispatch('fetchStandmansRating', payload)
        if (!state.loading.activeStandmans) dispatch('fetchActiveStandmans', payload)
        if (!state.loading.surveysFilled) dispatch('fetchSurveys', payload)
    },
    async fetchTotal({commit, state}, payload) {
        commit('setLoading', {total: true})
        commit('setError', {total: false})
        try {
            const {data} = await api.go('fetchTotal', payload)
            state.total = data.data
        } catch (e) {
            commit('setError', {total: true})
        } finally {
            commit('setLoading', {total: false})
        }
    },
    async fetchSurveys({commit, state}, payload) {
        commit('setLoading', {surveysFilled: true})
        commit('setError', {surveysFilled: false})
        try {
            const {data} = await api.go('fetchSurveys', payload)
            state.surveysFilled = data.data
        } catch (e) {
            commit('setError', {surveysFilled: true})
        } finally {
            commit('setLoading', {surveysFilled: false})
        }
    },
    async fetchMailing({commit, state}, payload) {
        commit('setLoading', {mailingFilled: true})
        commit('setError', {mailingFilled: false})
        try {
            const {data} = await api.go('getPageStatisticsSummary', payload)
            state.mailingFilled = data.data
        } catch (e) {
            commit('setError', {mailingFilled: true})
        } finally {
            commit('setLoading', {mailingFilled: false})
        }
    },
    async fetchActiveStandmans({commit, state}, payload) {
        commit('setLoading', {activeStandmans: true})
        commit('setError', {activeStandmans: false})
        try {
            const {data} = await api.go('fetchActiveStandmans', payload)
            state.activeStandmans = data.data
        } catch (e) {
            commit('setError', {activeStandmans: true})
        } finally {
            commit('setLoading', {activeStandmans: false})
        }
    },
    async fetchCreatedContacts({commit, state}, payload) {
        commit('setLoading', {createdContacts: true})
        commit('setError', {createdContacts: false})
        try {
            const {data} = await api.go('fetchCreatedContacts', payload)
            state.createdContacts = data.data
        } catch (e) {
            commit('setError', {createdContacts: true})
        } finally {
            commit('setLoading', {createdContacts: false})
        }
    },
    async fetchStandmansRating({commit, state}, payload) {
        commit('setLoading', {standmansRating: true})
        commit('setError', {standmansRating: false})
        try {
            const {data} = await api.go('fetchStandmansRating', payload)
            state.standmansRating = data.data
        } catch (e) {
            commit('setError', {standmansRating: true})
        } finally {
            commit('setLoading', {standmansRating: false})
        }
    },
    async fetchShardAttributes({commit, state},payload) {
        commit('setLoading', {contactAttributes: true})
        commit('setError', {contactAttributes: false})
        try {
            const {data} = await api.go('fetchShardAttributes',payload)
            state.contactAttributes = data.data
        } catch (e) {
            commit('setError', {contactAttributes: true})
        } finally {
            commit('setLoading', {contactAttributes: false})
        }
    },
    async fetchProductScans({commit, state},payload) {
        commit('setLoading', {productScans: true})
        commit('setError', {productScans: false})
        try {
            const {data} = await api.go('fetchProductScans',payload)
            state.productScans = data.data
        } catch (e) {
            commit('setError', {productScans: true})
        } finally {
            commit('setLoading', {productScans: false})
        }
    },
    async fetchProductByThematics({commit, state}, payload) {
        commit('setLoading', {productByThematics: true})
        commit('setError', {productByThematics: false})
        try {
            const {data} = await api.go('fetchProductByThematics', payload)
            state.productByThematics = data.data
        } catch (e) {
            commit('setError', {productByThematics: true})
        } finally {
            commit('setLoading', {productByThematics: false})
        }
    },
    async fetchStandmansContacts({commit, state}, payload) {
        commit('setLoading', {standmansContacts: true})
        commit('setError', {standmansContacts: false})
        try {
            const {data} = await api.go('fetchStandmansContacts', payload)
            state.standmansContacts = data.data
        } catch (e) {
            commit('setError', {standmansContacts: true})
        } finally {
            commit('setLoading', {standmansContacts: false})
        }
    },
    async fetchStandmansSurveys({commit, state}, payload) {
        commit('setLoading', {standmansSurveys: true})
        commit('setError', {standmansSurveys: false})
        try {
            const {data} = await api.go('fetchStandmansSurveys', payload)
            state.standmansSurveys = data.data
        } catch (e) {
            commit('setError', {standmansSurveys: true})
        } finally {
            commit('setLoading', {standmansSurveys: false})
        }
    },
    loadSurveysFromServer({dispatch, state}, payload) {
        if (!state.loading.surveysFilled) dispatch('fetchSurveys', payload)
    },
    loadStandmans({dispatch, state}, payload) {
        if (!state.loading.standmansContacts) dispatch('fetchStandmansContacts', payload)
        if (!state.loading.standmansSurveys) dispatch('fetchStandmansSurveys', payload)
    },
    loadScannings({dispatch}, payload) {
        dispatch('fetchProductScans', payload)
        dispatch('fetchProductByThematics', payload)
    }
}

export default {
    namespaced: true,
    state: defaultState,
    getters,
    mutations,
    actions
}
